"use client";

import type * as React from "react";
import { useState } from "react";
import apiClient from "@/api-client";
import { CircularProgress } from "@mui/material";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import { useColorScheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { GearSixIcon, LockKeyIcon, UserIcon } from "@phosphor-icons/react";
import { useLocation, useNavigate } from "react-router-dom";

import { DashboardLayout, DashboardNavColor, Settings } from "@/types/settings";
import { appConfig } from "@/config/app";
import { dashboardConfig } from "@/config/dashboard";
import { logger } from "@/lib/default-logger";
import { setSettings as setPersistedSettings } from "@/lib/settings";
import { useAuth } from "@/components/core/auth-provider";
import { useSettings } from "@/components/core/settings/settings-context";
import { SettingsDrawer } from "@/components/core/settings/settings-drawer";
import { Mode } from "@/styles/theme/types";

import { UserProfileDrawer } from "./userProfileDrawer";

export interface UserPopoverProps {
	anchorEl: null | Element;
	onClose?: () => void;
	open: boolean;
}

export function UserPopover({ anchorEl, onClose, open }: UserPopoverProps): React.JSX.Element {
	const navigate = useNavigate();
	const { user, account, setStartUrl } = useAuth();
	const { settings, setSettings } = useSettings();
	const { mode, setMode } = useColorScheme();

	const pathname = useLocation().pathname;
	const isSettingsTwoFactorActive = pathname.includes("/settings/two-factor");

	const [openUserProfileDrawer, setOpenUserProfileDrawer] = useState<boolean>(false);
	const [openAppSettingsDrawer, setOpenAppSettingsDrawer] = useState<boolean>(false);

	const [isSigningOut, setIsSigningOut] = useState<boolean>(false);

	const handleUpdate = async (values: Partial<Settings> & { theme?: Mode }): Promise<void> => {
		const { theme, ...other } = values;

		if (theme) {
			setMode(theme);
		}

		const updatedSettings = { ...settings, ...other } satisfies Settings;

		setPersistedSettings(updatedSettings);
		setSettings(updatedSettings);
	};

	const handleReset = async (): Promise<void> => {
		setMode(null);
		setPersistedSettings({});
		setSettings({});
	};

	const handleSignOut = async () => {
		try {
			setIsSigningOut(true);
			await apiClient.post("/auth/logout");
		} catch (error) {
			logger.error(error);
		} finally {
			localStorage.removeItem("token");
			localStorage.removeItem("user");
			navigate("/sign-in");
			setStartUrl("/");
			setIsSigningOut(false);
		}
	};

	return (
		<>
			<Popover
				anchorEl={anchorEl}
				anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
				onClose={onClose}
				open={Boolean(open)}
				slotProps={{ paper: { sx: { width: "280px" } } }}
				transformOrigin={{ horizontal: "right", vertical: "top" }}
			>
				<Box sx={{ p: 2 }}>
					<Typography>{user?.name}</Typography>
					<Typography color="text.secondary" variant="body2">
						{user?.email}
					</Typography>
				</Box>

				<Divider />

				<Box sx={{ p: 1 }}>
					<MenuItem
						component="div"
						onClick={() => {
							setOpenUserProfileDrawer(true);
							onClose?.();
						}}
						sx={{ justifyContent: "flex-start", gap: 1, fontSize: "16px" }}
					>
						<UserIcon fontSize="18px" />
						Profile
					</MenuItem>

					{account?.type === "smb" ? null : (
						<MenuItem
							component="div"
							onClick={() => {
								navigate("/settings/two-factor");
								onClose?.();
							}}
							sx={{
								justifyContent: "flex-start",
								gap: 1,
								fontSize: "16px",
								backgroundColor: isSettingsTwoFactorActive ? "var(--mui-palette-primary-main)" : "inherit",
								color: isSettingsTwoFactorActive ? "var(--mui-palette-primary-contrastText)" : "inherit",

								"&:hover": {
									backgroundColor: isSettingsTwoFactorActive ? "var(--mui-palette-primary-main)" : "action.hover",
									color: isSettingsTwoFactorActive ? "var(--mui-palette-primary-contrastText)" : "inherit",
								},
							}}
						>
							<LockKeyIcon fontSize="18px" />
							Two-Factor Authentication
						</MenuItem>
					)}

					<MenuItem
						component="div"
						onClick={() => {
							setOpenAppSettingsDrawer(true);
							onClose?.();
						}}
						sx={{ justifyContent: "flex-start", gap: 1, fontSize: "16px" }}
					>
						<GearSixIcon fontSize="18px" />
						App Settings
					</MenuItem>
				</Box>

				<Divider />

				<Box sx={{ p: 1 }}>
					<MenuItem
						component="div"
						sx={{ justifyContent: "center", fontSize: "16px", height: "36px" }}
						onClick={handleSignOut}
						disabled={isSigningOut}
					>
						{isSigningOut ? <CircularProgress size={20} color="inherit" /> : "Sign out"}
					</MenuItem>
				</Box>
			</Popover>

			<SettingsDrawer
				onClose={() => {
					setOpenAppSettingsDrawer(false);
				}}
				onReset={handleReset}
				onUpdate={handleUpdate}
				open={openAppSettingsDrawer}
				values={{
					direction: settings.direction ?? appConfig.direction,
					theme: mode,
					primaryColor: settings.primaryColor ?? appConfig.primaryColor,
					dashboardLayout: (settings.dashboardLayout as DashboardLayout) ?? dashboardConfig.layout,
					dashboardNavColor: (settings.dashboardNavColor as DashboardNavColor) ?? dashboardConfig.navColor,
				}}
			/>

			<UserProfileDrawer
				isUserProfileDrawerOpen={openUserProfileDrawer}
				onUserProfileDrawerClose={() => setOpenUserProfileDrawer(false)}
			/>
		</>
	);
}
